import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowRight } from "@phosphor-icons/react";
import { useNoIndex } from "../lib/usePageMeta";

export const Login = () => {
  useNoIndex("/login", {
    title: "Sign in",
    description: "Sign in to your Medical Peptide Store account to view orders, shipment tracking and saved details for research-use-only purchases.",
  });
  const { login } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(form.email, form.password);
      toast.success(`Welcome back, ${u.full_name}`);
      nav(u.role === "admin" ? "/admin" : "/account");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-24" data-testid="login-page" data-clarity-mask="true">
      <div className="overline mb-3">Access</div>
      <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-brand-navy">Sign in</h1>
      <p className="text-brand-muted text-sm mt-2">Sign in to review your orders and account details.</p>

      <form onSubmit={submit} className="mt-10 space-y-5">
        <div>
          <Label>Email</Label>
          <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email-input" />
        </div>
        <div>
          <Label>Password</Label>
          <Input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password-input" />
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid="login-submit-btn"
          className="w-full inline-flex items-center justify-center gap-2 bg-brand-navy text-white hover:bg-brand-navy2 font-semibold px-6 py-3 text-sm tracking-wide transition-colors disabled:opacity-50"
        >
          {busy ? "Signing in…" : "Sign in"} <ArrowRight size={16} weight="bold" />
        </button>
      </form>
      <div className="mt-8 text-sm text-brand-muted">
        No account yet?{" "}
        <Link to="/register" className="text-brand-accent hover:text-brand-accent" data-testid="login-register-link">Create one</Link>
      </div>
    </div>
  );
};

export const Register = () => {
  useNoIndex("/register", {
    title: "Create an account",
    description: "Create a Medical Peptide Store account to track orders and manage research-use-only purchases of lyophilized reference standards.",
  });
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", full_name: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form);
      toast.success("Account created. Consider applying for verification.");
      nav("/account");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-24" data-testid="register-page" data-clarity-mask="true">
      <div className="overline mb-3">Access</div>
      <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-brand-navy">Create an account</h1>
      <p className="text-brand-muted text-sm mt-2">Individual catalog access. Verification required for purchasing.</p>

      <form onSubmit={submit} className="mt-10 space-y-5">
        <div>
          <Label>Full name</Label>
          <Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="register-fullname-input" />
        </div>
        <div>
          <Label>Email</Label>
          <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email-input" />
        </div>
        <div>
          <Label>Password (min 8 chars)</Label>
          <Input required minLength={8} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="register-password-input" />
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid="register-submit-btn"
          className="w-full inline-flex items-center justify-center gap-2 bg-brand-navy text-white hover:bg-brand-navy2 font-semibold px-6 py-3 text-sm tracking-wide transition-colors disabled:opacity-50"
        >
          {busy ? "Creating…" : "Create account"} <ArrowRight size={16} weight="bold" />
        </button>
      </form>
      <div className="mt-8 text-sm text-brand-muted">
        Already have an account?{" "}
        <Link to="/login" className="text-brand-accent hover:text-brand-accent" data-testid="register-login-link">Sign in</Link>
      </div>
    </div>
  );
};
